import { useCallback, useEffect, useRef, useState } from 'react'
import { useSnapshot } from 'valtio'
import {
  CurrentUserAssessmentState,
  CurrentTimerState,
  setRemainedTime,
  startTimer,
  resumeTimer,
  IdleDetectorState,
  endTimer,
  CurrentTestState,
  CurrentQtiPackageState,
  setIdleStatus,
  setPassedTime,
  setIsUnlimited,
} from '@app/storage'
import { TimerStatus } from '@app/types'
import { AssessmentStatus } from '@app/firebase/api'
import { Idle } from 'idlejs'

export const useDefineTimeLimit = (): number | null => {
  const { currentTest } = useSnapshot(CurrentTestState)
  const { assessmentId, userAssessmentId } = useSnapshot(CurrentUserAssessmentState)
  const [timeLimit, setTimeLimit] = useState<number>(null)

  useEffect(() => {
    if (!assessmentId && !userAssessmentId) {
      return
    }

    const customTimeLimit = CurrentUserAssessmentState.assessmentSettings?.timeLimits

    if (customTimeLimit) {
      setTimeLimit(customTimeLimit)
      return
    }

    if (currentTest?.timeLimits?.maxTime) {
      setTimeLimit(currentTest?.timeLimits?.maxTime)
      return
    }

    setTimeLimit(0)
  }, [assessmentId, userAssessmentId, currentTest])

  return timeLimit
}

export const useTimerSetup = (): void => {
  const maxTime = useDefineTimeLimit()
  const { passedTime, addedTime, assessmentId, status, assessmentSettings, startedTimestamp } =
    useSnapshot(CurrentUserAssessmentState)
  const { remainedTime, timerStatus, isUnlimited } = useSnapshot(CurrentTimerState)
  const { isIdle } = useSnapshot(IdleDetectorState)
  const [initialRemainedTime, setInitialRemainedTime] = useState<number>(null)

  useEffect(() => {
    if (
      !assessmentSettings ||
      !assessmentId ||
      passedTime === null ||
      !startedTimestamp ||
      maxTime === null
    ) {
      return
    }

    const currentDate = new Date()
    const addedTimeNumber = CurrentUserAssessmentState?.addedTime ?? 0

    const passedTimeNumber = CurrentUserAssessmentState.assessmentSettings.isResumable
      ? (CurrentUserAssessmentState.passedTime ?? 0)
      : Math.round(
          (currentDate.getTime() - CurrentUserAssessmentState.startedTimestamp.toDate().getTime()) / 1000,
        )

    setPassedTime(passedTimeNumber)

    // For unlimited tests (maxTime === 0), don't calculate remaining time
    if (maxTime === 0) {
      setIsUnlimited(true)
      setInitialRemainedTime(0)
      return
    }

    setInitialRemainedTime(maxTime + addedTimeNumber - passedTimeNumber)
  }, [maxTime, assessmentId, passedTime, addedTime, assessmentSettings, startedTimestamp])

  useEffect(() => {
    if (status !== AssessmentStatus.completed) {
      return
    }

    endTimer()
  }, [status])

  useEffect(() => {
    // Check if timer should be initialized
    // For unlimited tests (initialRemainedTime === 0), we should skip timer setup
    if (initialRemainedTime === null || timerStatus === TimerStatus.ended) {
      return
    }
    // Update remained time value if timer is not started or paused.
    if (!remainedTime) {
      console.log('[useInitialRemainedTimeSetup] setting initial remained time...', initialRemainedTime)
      setRemainedTime(initialRemainedTime)
    }

    if (isIdle) return

    if (timerStatus === TimerStatus.notStarted) {
      startTimer(isUnlimited)
      return
    }

    if (timerStatus === TimerStatus.paused) {
      resumeTimer()
      return
    }
  }, [initialRemainedTime, remainedTime, timerStatus, isIdle, isUnlimited])
}

export const useTimeoutAction = (allowLateSubmission: boolean, onTimeOut: () => void) => {
  const { remainedTime, isUnlimited } = useSnapshot(CurrentTimerState)

  useEffect(() => {
    if (remainedTime === null || isUnlimited) {
      return
    }

    if (remainedTime <= 0) {
      console.log('[useTimeoutAction] time is out...')
      endTimer()
      !allowLateSubmission && onTimeOut()
    }
  }, [remainedTime, allowLateSubmission, onTimeOut, isUnlimited])
}

export const useIdleDetection = () => {
  const { assessmentSettings } = useSnapshot(CurrentQtiPackageState)
  const { isIdle, modalOpened } = useSnapshot(IdleDetectorState)
  const idleDetector = useRef<Idle>(null)

  const onIdleDetection = useCallback(() => {
    if (isIdle) {
      return
    }

    setIdleStatus(true)
  }, [isIdle])

  useEffect(() => {
    const { maxIdletime, isResumable } = assessmentSettings || {}

    if (!maxIdletime || !isResumable) {
      return
    }

    if (!idleDetector.current) {
      idleDetector.current = new Idle()
        .whenNotInteractive()
        .within(assessmentSettings?.maxIdletime)
        .do(() => onIdleDetection())
        .start()

      return
    }

    const isActive = !isIdle && !modalOpened

    if (isActive) {
      idleDetector.current.stop()
      idleDetector.current.start()
    } else {
      idleDetector.current.stop()
    }

    return () => {
      if (idleDetector.current) {
        idleDetector.current.stop()
      }
    }
  }, [isIdle, modalOpened, assessmentSettings, onIdleDetection])
}
